import { useEffect, useState } from 'react';
import { useGlobalStore } from '../../../application/store/store.tsx';
import StorageService from '../../storageManagement/services/StorageService.ts';
import { StorageLocation } from '../../../application/types/storageLocation.ts';
import { DrugByBarCodeResponse } from '../../../application/types/drug.ts';
import DrugService from '../../drugManagement/services/DrugService.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye } from '@fortawesome/free-solid-svg-icons';
import { LinearProgress } from '@mui/material';
import { getDrugDisplayName } from '../../../common/utils/getDrugDisplayName.ts';
import Visibility from '../../../common/components/Visibility.tsx';

const ScanningDetector = ({
    steps,
    activeStep,
    onScanComplete,
}: {
    steps: string[];
    activeStep: number;
    onScanComplete: () => void;
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [synonymInfo, setSynonymInfo] = useState<string | null>(null);
    const {
        barCodeState,
        resetBarCodeState,
        setSelectedStorage,
        setSelectedEmergency,
        setSelectedDrug,
        setMessage,
        selectedStorage,
        selectedEmergency,
        selectedDrug,
    } = useGlobalStore();

    const storageService = new StorageService();
    const drugService = new DrugService();

    useEffect(() => {
        if (
            steps[activeStep] === 'Lagerställe' ||
            steps[activeStep] === 'Läkemedelsskåp' ||
            steps[activeStep] === 'Akutväska'
        ) {
            if (barCodeState !== null) {
                fetchStorageFromBarCode().then((storage) => {
                    if (storage === null || storage === undefined) {
                        setMessage('Lagerstället kan inte hittas', 'error');
                    } else {
                        onSetSelectStorage(storage, steps[activeStep] as any);
                    }
                });
            }
        } else if (steps[activeStep] === 'Läkemedel') {
            if (barCodeState !== null) {
                fetchDrugFromBarCode().then((response) => {
                    if (response === null || response === undefined) {
                        setMessage('Läkemedlet kan inte hittas', 'error');
                    } else {
                        onSetSelectedDrug(response);
                    }
                });
            }
        }
        return () => {
            resetBarCodeState();
        };
    }, [barCodeState]);

    const fetchStorageFromBarCode = async (): Promise<StorageLocation> => {
        try {
            setIsLoading(true);
            return await storageService.getStorageByBarCode(barCodeState);
        } catch (error) {
            resetBarCodeState();
            setMessage('Lagerstället kan inte hittas', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDrugFromBarCode =
        async (): Promise<DrugByBarCodeResponse | null> => {
            try {
                setIsLoading(true);
                return await drugService.getDrugByBarcode(barCodeState);
            } catch (error) {
                resetBarCodeState();
                setMessage('Läkemedlet kan inte hittas', 'error');
                return null;
            } finally {
                setIsLoading(false);
            }
        };

    const onSetSelectedDrug = (response: DrugByBarCodeResponse) => {
        const { drug, matchedSynonymId } = response;

        // If matched via synonym, find and display synonym info
        if (matchedSynonymId && drug.synonyms) {
            const synonym = drug.synonyms.find(
                (s) => s.id === matchedSynonymId
            );
            if (synonym) {
                const synonymDisplay = synonym.produktName
                    ? `${synonym.produktName} (${synonym.barCode || synonym.gtin})`
                    : synonym.barCode || synonym.gtin;
                setSynonymInfo(`Matchad via synonym: ${synonymDisplay}`);
            }
        } else {
            setSynonymInfo(null);
        }

        setSelectedDrug(drug, matchedSynonymId);
        resetBarCodeState();
        onScanComplete();
    };

    const onSetSelectStorage = (
        storage: StorageLocation,
        storageType: 'Läkemedelsskåp' | 'Akutväska' | 'Lagerställe'
    ) => {
        if (storageType === 'Lagerställe' || storageType === 'Läkemedelsskåp') {
            setSelectedStorage(storage);
        }
        if (storageType === 'Akutväska') {
            setSelectedEmergency(storage);
        }
        resetBarCodeState();
        onScanComplete();
    };

    return (
        <div className="flex flex-col items-center justify-center">
            <div className="text-center translate-y-28">
                <div className="">
                    <h1 className="text-7xl mt-5">{activeStep + 1}</h1>
                    <h1 className="text-4xl mt-4">
                        Scanna {steps[activeStep]}
                    </h1>
                </div>
                <div className="translate-y-1">
                    <Visibility
                        visible={
                            steps[activeStep] === 'Lagerställe' ||
                            steps[activeStep] === 'Läkemedelsskåp'
                        }
                    >
                        <div>
                            <h3 className="text-2xl">
                                {selectedStorage?.name}
                            </h3>
                        </div>
                    </Visibility>
                    <Visibility visible={steps[activeStep] === 'Akutväska'}>
                        <div>
                            <h3 className="text-2xl">
                                {selectedEmergency?.name}
                            </h3>
                        </div>
                    </Visibility>
                    <Visibility visible={steps[activeStep] === 'Läkemedel'}>
                        <div>
                            <h3 className="text-2xl">
                                {getDrugDisplayName(selectedDrug)}
                            </h3>
                            {synonymInfo && (
                                <p className="text-sm text-blue-600 mt-1">
                                    {synonymInfo}
                                </p>
                            )}
                        </div>
                    </Visibility>
                    <Visibility visible={isLoading}>
                        <LinearProgress />
                    </Visibility>
                </div>
                <FontAwesomeIcon
                    icon={faBullseye}
                    size="10x"
                    color="gray"
                    className="opacity-5 text-gray-20"
                />
            </div>
        </div>
    );
};
export default ScanningDetector;
